import { useEffect, useRef, useState, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { mapStyle, bearingController } from '../map/maplibre';
import { createTrainMarker, updateMarkerPosition } from '../map/trainIcon';
import { startWatching, type GpsPosition } from '../sensors/gps';
import { startHeadingTracking, type HeadingData } from '../sensors/heading';
import { poiLayer } from '../map/poi-layer';
import { gpxRecorder } from '../data/gpx-recorder';
interface MapViewProps {
  follow: boolean;
  onFollowChange: (follow: boolean) => void;
  onCenterRequest: () => void;
  onPositionUpdate?: (position: GpsPosition | null) => void;
  onHeadingUpdate?: (heading: HeadingData | null) => void;
  isSimulating?: boolean;
  position?: GpsPosition | null;
}

export function MapView({ follow, onFollowChange, onPositionUpdate, onHeadingUpdate, isSimulating, position }: MapViewProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const marker = useRef<maplibregl.Marker | null>(null);
  const [userPosition, setUserPosition] = useState<GpsPosition | null>(null);
  const [gpsError, setGpsError] = useState<string | null>(null);
  const [waitingForGPS, setWaitingForGPS] = useState(true);
  const gpsTimeoutRef = useRef<number | null>(null);
  const zoomInterruptTimeRef = useRef<number>(0);
  const nightModeCheckRef = useRef<number | null>(null);
  // Charger la préférence POI depuis le cookie, par défaut true
  const [showPoi, setShowPoi] = useState<boolean>(() => {
    const savedPref = localStorage.getItem('showPoi');
    return savedPref === null ? true : savedPref === 'true';
  });
  // State pour l'enregistrement GPX
  const [isRecordingGpx, setIsRecordingGpx] = useState(false);
  const [gpxStats, setGpxStats] = useState({ pointCount: 0, duration: '0:00' });
  
  // Charger la préférence de thème depuis localStorage, par défaut 'auto'
  const [themeMode, setThemeMode] = useState<'day' | 'night' | 'auto'>(() => {
    const savedTheme = localStorage.getItem('themeMode');
    return (savedTheme as 'day' | 'night' | 'auto') || 'auto';
  });
  const isUserInteracting = useRef(false);
  const lastInteractionTime = useRef<number>(0);
  const lastMapUpdate = useRef<number>(0);
  const headingManagerRef = useRef<{ updateFromGps: (data: { lat: number; lon: number; speed: number | null; timestamp: number }) => void; destroy: () => void } | null>(null);
  const simulationFirstPoint = useRef(true);
  const simulationInitialCenteringTime = useRef<number>(0);
  useEffect(() => {
    if (!mapContainer.current) return;

    try {
      map.current = new maplibregl.Map({
        container: mapContainer.current,
        style: mapStyle, // Style initial
        center: [2.8851, 50.4358], // Gare d'Arras au lieu de Paris
        zoom: localStorage.getItem('mapZoom') ? parseFloat(localStorage.getItem('mapZoom')!) : 15, // Zoom sauvegardé ou 15 par défaut
        maxZoom: 17,
        // Optimisations WebGL pour la fluidité
        renderWorldCopies: false, // Éviter les copies multiples du monde
        crossSourceCollisions: false, // Désactiver les collisions entre sources pour la performance
        refreshExpiredTiles: false // Ne pas rafraîchir automatiquement les tuiles expirées
      });
    } catch (error) {
      console.error('Erreur de création de la carte:', error);
      setGpsError('Erreur WebGL: Impossible d\'initialiser la carte. Votre navigateur ne supporte peut-être pas WebGL ou il y a un problème de pilote graphique.');
      return;
    }

    try {
      bearingController.setMap(map.current);
      
      // Fonction pour initialiser POI layer
      let poiInitialized = false;
      const initPOILayer = () => {
        if (poiInitialized) return; // Éviter les initialisations multiples
        
        if (map.current && map.current.isStyleLoaded() && !map.current.getSource('railway-pois-source')) {
          console.log('🗺️ Initialisation du POI layer...');
          try {
            poiLayer.setMap(map.current);
            poiInitialized = true;
            
            // Force l'affichage des POI immédiatement si activé
            if (showPoi) {
              console.log('🔄 Activation immédiate des POI...');
              poiLayer.show();
              console.log('✅ POI layer initialisé et activé');
              
              // Force un rechargement des PK après un délai plus long
              setTimeout(() => {
                console.log('🔄 Force reload PK initial après 2s');
                if (poiLayer.isVisible()) {
                  poiLayer.forceReloadPK();
                }
              }, 2000);
            } else {
              poiLayer.hide();
              console.log('✅ POI layer initialisé mais masqué');
            }
          } catch (error) {
            console.error('❌ Erreur lors de l\'initialisation POI:', error);
            poiInitialized = false; // Permettre un retry en cas d'erreur
          }
        } else {
          console.log('⏳ Style pas encore prêt pour POI layer');
        }
      };

      
      // Essayer d'initialiser immédiatement si le style est déjà chargé
      if (map.current.isStyleLoaded()) {
        initPOILayer();
      }
      
      // Écouter aussi l'événement styledata pour être sûr
      map.current.on('styledata', initPOILayer);
      
      // Et aussi l'événement load au cas où
      map.current.on('load', () => {
        console.log('🗺️ Carte chargée, tentative d\'init POI...');
        initPOILayer();
      });
      
      // Sauvegarder le zoom quand il change
      map.current.on('zoomend', () => {
        if (map.current) {
          const zoom = map.current.getZoom();
          localStorage.setItem('mapZoom', zoom.toString());
          console.log('💾 Zoom sauvegardé:', zoom.toFixed(1));
        }
      });
      
      // Vérification périodique du mode nuit toutes les minutes SEULEMENT en mode auto
      const startNightModeCheck = () => {
        if (nightModeCheckRef.current) clearInterval(nightModeCheckRef.current);
        nightModeCheckRef.current = window.setInterval(() => {
          // Ne vérifier que si on est en mode auto
          const currentTheme = localStorage.getItem('themeMode') || 'auto';
          if (currentTheme === 'auto') {
            checkAndUpdateNightMode();
          }
        }, 60000); // 60 secondes
      };
      
      startNightModeCheck();

      const trainMarker = createTrainMarker();
      marker.current = new maplibregl.Marker({ element: trainMarker })
        .setLngLat([0, 0])
        .addTo(map.current);

      trainMarker.style.display = 'none';

      // Seul le drag de l'utilisateur doit désactiver le Follow
      map.current.on('dragstart', () => {
        isUserInteracting.current = true;
        lastInteractionTime.current = Date.now();
        onFollowChange(false);
      });
      
      // On pourrait ajouter le pinch/zoom tactile si besoin
      map.current.on('touchstart', (e) => {
        // Si plus d'un doigt (pinch zoom), désactiver follow
        if (e.originalEvent.touches && e.originalEvent.touches.length > 1) {
          isUserInteracting.current = true;
          lastInteractionTime.current = Date.now();
          onFollowChange(false);
        }
      });
    } catch (error) {
      console.error('Erreur lors de l\'initialisation de la carte:', error);
      setGpsError('Erreur lors de l\'initialisation de la carte: ' + (error as Error).message);
      return;
    }

    return () => {
      try {
        bearingController.destroy();
        poiLayer.destroy();
        if (nightModeCheckRef.current) {
          clearInterval(nightModeCheckRef.current);
        }
        map.current?.remove();
      } catch (error) {
        console.error('Erreur lors du nettoyage:', error);
      }
    };
  }, [onFollowChange]);

  // Effet pour mettre à jour les statistiques GPX
  useEffect(() => {
    let statsInterval: number | null = null;
    
    if (isRecordingGpx) {
      statsInterval = window.setInterval(() => {
        const stats = gpxRecorder.getStats();
        setGpxStats({
          pointCount: stats.pointCount,
          duration: stats.duration
        });
      }, 1000);
    }
    
    return () => {
      if (statsInterval) {
        clearInterval(statsInterval);
      }
    };
  }, [isRecordingGpx]);

  // Fonction pour déterminer si on doit être en mode nuit selon les préférences
  const shouldUseNightMode = useCallback(() => {
    if (themeMode === 'night') return true;
    if (themeMode === 'day') return false;
    
    // Mode auto : selon l'heure
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const currentTimeMinutes = hours * 60 + minutes;
    const nightStartMinutes = 20 * 60 + 15; // 20h15
    const nightEndMinutes = 6 * 60; // 6h00
    
    return currentTimeMinutes >= nightStartMinutes || currentTimeMinutes < nightEndMinutes;
  }, [themeMode]);

  // Fonction pour vérifier et mettre à jour le mode nuit
  const checkAndUpdateNightMode = useCallback(() => {
    if (!map.current) return;
    
    try {
      const shouldBeNight = shouldUseNightMode();
      const mapStyle = map.current.getStyle();
      
      // Vérifier que le style et les sources existent
      if (!mapStyle || !mapStyle.sources || !mapStyle.sources.osm) {
        console.warn('⚠️ Style de carte pas encore chargé, on attend...');
        return;
      }
      
      const currentSource = mapStyle.sources.osm as any;
      if (!currentSource.tiles || !currentSource.tiles[0]) {
        console.warn('⚠️ Tiles pas encore définies, on attend...');
        return;
      }
      
      const isCurrentlyNight = currentSource.tiles[0].includes('cartodb');
      
      if (shouldBeNight !== isCurrentlyNight) {
        const modeText = themeMode === 'auto' ? (shouldBeNight ? '🌙 Auto->Nuit' : '☀️ Auto->Jour') 
                                              : (shouldBeNight ? '🌙 Manuel->Nuit' : '☀️ Manuel->Jour');
        console.log(modeText);
        
        // Importer dynamiquement le nouveau style avec forçage du mode
        import('../map/maplibre').then((module) => {
          const newStyle = shouldBeNight ? module.getNightStyle() : module.getDayStyle();
          
          if (map.current) {
            // Sauvegarder l'état POI avant le changement de style
            const wasPoiVisible = poiLayer.isVisible();
            
            // Changer le style
            map.current.setStyle(newStyle);
            
            // Réinitialiser les POI après le chargement du nouveau style
            map.current.once('styledata', () => {
              console.log('🎨 Style changé, réinitialisation des POI...');
              console.log('🔍 wasPoiVisible:', wasPoiVisible);
              try {
                // Réinitialiser le POI layer avec le nouveau style
                poiLayer.setMap(map.current!);
                
                // Restaurer la visibilité si elle était active
                if (wasPoiVisible) {
                  console.log('⏰ Restauration POI dans 2s...');
                  setTimeout(() => {
                    console.log('🔄 Déclenchement restauration POI...');
                    poiLayer.show();
                    // Mettre à jour le state React pour synchroniser l'UI
                    setShowPoi(true);
                    console.log('✅ POI réactivés et state synchronisé');
                  }, 2000); // Encore plus de délai
                } else {
                  console.log('ℹ️ POI n\'était pas visible, pas de restauration');
                }
              } catch (error) {
                console.error('❌ Erreur réinitialisation POI:', error);
              }
            });
          }
        });
      }
    } catch (error) {
      console.error('❌ Erreur dans checkAndUpdateNightMode:', error);
    }
  }, [shouldUseNightMode, themeMode]);

  // Fonction commune pour traiter les positions (GPS réel ou simulé)  
  const handlePosition = useCallback((position: GpsPosition) => {
    // Premier fix GPS reçu
    if (waitingForGPS && !isSimulating) {
      setWaitingForGPS(false);
      if (gpsTimeoutRef.current) {
        clearTimeout(gpsTimeoutRef.current);
        gpsTimeoutRef.current = null;
      }
      // Centrer sur la position GPS
      if (map.current) {
        map.current.setCenter([position.lon, position.lat]);
        map.current.setZoom(15);
        console.log('🎯 Premier fix GPS, centrage sur:', position.lat, position.lon);
      }
    }
    
    setUserPosition(position);
    setGpsError(null);
    onPositionUpdate?.(position);
    
    // Enregistrer la position GPX en temps réel (seulement si pas en simulation)
    if (!isSimulating) {
      gpxRecorder.addPosition(position);
    }

    // Note: la mise à jour du marker est gérée dans le useEffect suivant pour éviter la duplication

    if (headingManagerRef.current) {
      headingManagerRef.current.updateFromGps({
        lat: position.lat,
        lon: position.lon,
        speed: position.speed,
        timestamp: position.timestamp
      });
    }
  }, [onPositionUpdate, waitingForGPS, isSimulating]);

  useEffect(() => {
    headingManagerRef.current = startHeadingTracking({
      onHeading: (data) => {
        onHeadingUpdate?.(data);
        // Activer le bearing pour tous les modes maintenant que l'alignement est fixé
        bearingController.updateBearing(data);
      }
    });

    // Démarrer GPS réel seulement si pas en simulation
    let gps: { stop: () => void } | null = null;
    
    if (!isSimulating) {
      gps = startWatching({
        onPosition: handlePosition,
        onError: (error) => {
          setGpsError(error.message);
          console.error('GPS Error:', error);
        }
      });
      
      // Timeout de 5 secondes pour le GPS
      if (waitingForGPS) {
        gpsTimeoutRef.current = window.setTimeout(() => {
          if (waitingForGPS) {
            setWaitingForGPS(false);
            console.log('⏰ Timeout GPS - reste sur Arras');
            // La carte est déjà centrée sur Arras par défaut
          }
        }, 5000);
      }
    }

    return () => {
      headingManagerRef.current?.destroy();
      gps?.stop();
      if (gpsTimeoutRef.current) {
        clearTimeout(gpsTimeoutRef.current);
      }
    };
  }, [follow, onPositionUpdate, onHeadingUpdate, isSimulating, handlePosition, waitingForGPS]);

  // Traiter les positions externes (simulation)
  useEffect(() => {
    if (position && isSimulating) {
      // La simulation supprime le message de chargement
      if (waitingForGPS) {
        setWaitingForGPS(false);
        if (gpsTimeoutRef.current) {
          clearTimeout(gpsTimeoutRef.current);
          gpsTimeoutRef.current = null;
        }
      }
      
      handlePosition(position);
      
      // Centre la carte SEULEMENT au tout premier point de simulation
      if (map.current && simulationFirstPoint.current) {
        simulationFirstPoint.current = false;
        simulationInitialCenteringTime.current = Date.now();
        map.current.setCenter([position.lon, position.lat]);
        // Conserver le zoom actuel au lieu d'imposer zoom 12
        console.log(`🗺️ Simulation: centrage initial forcé sur lat=${position.lat.toFixed(6)}, lon=${position.lon.toFixed(6)}`);
      }
    } else if (!isSimulating) {
      // Reset pour la prochaine simulation
      simulationFirstPoint.current = true;
      simulationInitialCenteringTime.current = 0;
    }
  }, [position, isSimulating, handlePosition, waitingForGPS]);

  // Effet pour déclencher une vérification immédiate lors du changement de mode
  useEffect(() => {
    // Seulement déclencher si on change de mode, pas pour chaque vérification auto
    if (themeMode !== 'auto') {
      console.log(`🎨 Forçage thème manuel: ${themeMode}`);
    }
    checkAndUpdateNightMode();
  }, [themeMode, checkAndUpdateNightMode]);

  useEffect(() => {
    if (!userPosition || !map.current) return;
    
    const now = Date.now();
    const timeSinceLastUpdate = now - lastMapUpdate.current;
    
    // Throttling unifié pour éviter le clignotement
    let shouldThrottleUpdate = false;
    
    if (isSimulating) {
      // En simulation, synchroniser avec le bearing controller (20 Hz = 50ms)
      shouldThrottleUpdate = timeSinceLastUpdate < 50;
    } else {
      // En mode GPS réel, éviter les mises à jour trop fréquentes si vitesse faible
      const isSlowOrStationary = (userPosition.speed || 0) < 1.0;
      shouldThrottleUpdate = isSlowOrStationary && timeSinceLastUpdate < 2000;
    }
    
    if (shouldThrottleUpdate) return;

    // Mise à jour unique du marker avec throttling
    if (marker.current) {
      updateMarkerPosition(marker.current, userPosition, !isSimulating);
    }

    // Logique de centrage : train toujours en bas d'écran
    // Mais on évite le recentrage immédiat après le centrage initial de simulation
    // Et on interrompt après un zoom pendant 2 secondes
    const timeSinceInitialCentering = now - simulationInitialCenteringTime.current;
    const timeSinceZoom = now - zoomInterruptTimeRef.current;
    const shouldRecenter = (follow || (!isUserInteracting.current) || (now - lastInteractionTime.current > 15000)) && 
                          (!isSimulating || timeSinceInitialCentering > 2000) && // 2 secondes de grâce après centrage initial
                          (timeSinceZoom > 2000); // 2 secondes d'interruption après zoom

    if (shouldRecenter) {
        isUserInteracting.current = false;
        
        
        // Calculer l'offset pour placer le train selon le mode
        const mapElement = map.current.getContainer();
        const mapHeight = mapElement.offsetHeight;
        
        // CORRECTION ALIGNEMENT: Calculer l'offset pour aligner exactement la carte avec l'icône du train
        // Mode simulation: Train CSS à bottom: 14% = 86% depuis le haut, offset = 36%
        // Mode GPS réel: Utiliser le même principe d'alignement pour cohérence
        // Ancien offset réel était 0.41, on passe à 0.36 pour uniformiser
        const offsetY = mapHeight * 0.36;
        
        // Convertir l'offset pixel en coordonnées géographiques
        const centerPoint = map.current.project([userPosition.lon, userPosition.lat]);
        centerPoint.y -= offsetY; // Décalage standard
        const offsetCenter = map.current.unproject(centerPoint);
        
        // Adapter la durée d'animation selon si on est en simulation
        const animationDuration = isSimulating 
          ? (follow ? 200 : 800)  // Plus rapide en simulation
          : (follow ? 500 : 1500); // Durée normale
          
        map.current.easeTo({
          center: [offsetCenter.lng, offsetCenter.lat],
          zoom: Math.max(map.current.getZoom(), 12),
          duration: animationDuration,
          // Désactiver l'easing élastique pour éviter le rebond
          easing: (t) => t // Fonction d'easing linéaire au lieu de cubique
        });

        // Mettre à jour le timestamp de dernière mise à jour
        lastMapUpdate.current = now;

        // Réactiver le follow après recentrage automatique
        if (!follow && (now - lastInteractionTime.current > 15000)) {
          onFollowChange(true);
        }
    }
  }, [userPosition, follow, onFollowChange, isSimulating]);

  useEffect(() => {
    const centerOnUser = () => {
      if (userPosition && map.current) {
        isUserInteracting.current = false;
        lastInteractionTime.current = 0; // Force recentrage immédiat
        onFollowChange(true);
      }
    };

    window.addEventListener('centerOnUser', centerOnUser);
    return () => {
      window.removeEventListener('centerOnUser', centerOnUser);
    };
  }, [userPosition, onFollowChange]);

  return (
    <div className="relative w-full h-full">
      <div ref={mapContainer} className="w-full h-full" />
      
      {/* Message de chargement GPS */}
      {waitingForGPS && !isSimulating && (
        <div className="absolute inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="text-white text-center">
            <div className="mb-4">
              <svg className="animate-spin h-12 w-12 mx-auto text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <div className="text-lg">Recherche GPS...</div>
            <div className="text-sm text-gray-400 mt-2">Centrage sur Arras dans 5 secondes</div>
          </div>
        </div>
      )}
      
      {/* Train fixe en bas d'écran pendant la simulation */}
      {isSimulating && userPosition && (
        <div className="absolute left-1/2 transform -translate-x-1/2 pointer-events-none z-30"
             style={{ bottom: '14%', transform: 'translateX(-50%) translateY(51.5px)' }}>
          <img 
            src="/marker-train-fret-up.svg"
            alt="Train"
            className="w-10 h-[103px]"
            style={{
              filter: "drop-shadow(0 1px 2px rgba(0,0,0,.6)) drop-shadow(0 8px 16px rgba(0,0,0,.45))",
              transformOrigin: "50% 50%"
            }}
          />
        </div>
      )}
      
      {/* Contrôles de zoom */}
      <div className="absolute left-4 bottom-4 z-40 flex flex-col space-y-2">
        <button
          onClick={() => {
            if (map.current) {
              zoomInterruptTimeRef.current = Date.now();
              map.current.zoomIn();
              console.log('🔍 Zoom In - Interruption mises à jour position 2s');
            }
          }}
          className="p-3 bg-white/90 hover:bg-white text-gray-700 rounded-lg shadow-lg transition-all duration-200"
          title="Zoomer"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="9"/>
            <path d="M12 8v8"/>
            <path d="M8 12h8"/>
          </svg>
        </button>
        <button
          onClick={() => {
            if (map.current) {
              zoomInterruptTimeRef.current = Date.now();
              map.current.zoomOut();
              console.log('🔍 Zoom Out - Interruption mises à jour position 2s');
            }
          }}
          className="p-3 bg-white/90 hover:bg-white text-gray-700 rounded-lg shadow-lg transition-all duration-200"
          title="Dézoomer"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="9"/>
            <path d="M8 12h8"/>
          </svg>
        </button>
      </div>

      {/* Boutons à droite */}
      <div className="absolute right-4 top-32 z-40 flex flex-col space-y-2">
        {/* Bouton POI */}
        <button
          onClick={() => {
            console.log('🔘 Bouton POI cliqué');
            console.log('🔘 poiLayer existe?', !!poiLayer);
            
            if (!poiLayer) {
              console.error('❌ poiLayer n\'est pas défini !');
              return;
            }
            
            try {
              console.log('🔘 Appel de poiLayer.toggle()...');
              poiLayer.toggle();
              
              // Synchroniser l'état avec le poiLayer après le toggle
              const isVisible = poiLayer.isVisible();
              console.log('🔘 POI visible après toggle:', isVisible);
              setShowPoi(isVisible);
              // Sauvegarder la préférence dans localStorage
              localStorage.setItem('showPoi', isVisible.toString());
            } catch (error) {
              console.error('❌ Erreur lors du toggle POI:', error);
            }
          }}
          className={`p-3 rounded-lg shadow-lg transition-all duration-200 ${
            showPoi 
              ? 'bg-blue-600 text-white' 
              : 'bg-white/90 text-gray-700 hover:bg-white'
          }`}
          title={showPoi ? 'Masquer les gares/PK' : 'Afficher les gares/PK'}
        >
          <svg 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
          >
            <path d="M12 2L2 7l10 5 10-5-10-5z"/>
            <path d="M2 17l10 5 10-5"/>
            <path d="M2 12l10 5 10-5"/>
          </svg>
        </button>

        {/* Bouton Thème jour/nuit/auto */}
        <button
          onClick={() => {
            let newMode: 'day' | 'night' | 'auto';
            if (themeMode === 'auto') newMode = 'day';
            else if (themeMode === 'day') newMode = 'night';
            else newMode = 'auto';
            
            setThemeMode(newMode);
            localStorage.setItem('themeMode', newMode);
            console.log(`🎨 Changement thème: ${newMode}`);
            
            // Forcer la mise à jour immédiate
            checkAndUpdateNightMode();
          }}
          className="p-3 bg-white/90 hover:bg-white text-gray-700 rounded-lg shadow-lg transition-all duration-200 text-lg"
          title={`Thème: ${themeMode === 'auto' ? 'Automatique' : themeMode === 'day' ? 'Jour' : 'Nuit'}`}
        >
          {themeMode === 'auto' && (
            <span className="flex items-center justify-center text-sm">
              ☀️🌙
            </span>
          )}
          {themeMode === 'day' && '☀️'}
          {themeMode === 'night' && '🌙'}
        </button>

        {/* Bouton GPX Recording - seulement en mode temps réel */}
        {!isSimulating && (
          <button
            onClick={() => {
              if (isRecordingGpx) {
                gpxRecorder.stop();
                setIsRecordingGpx(false);
              } else {
                gpxRecorder.start();
                setIsRecordingGpx(true);
                setGpxStats({ pointCount: 0, duration: '0:00' });
              }
            }}
            className={`p-3 rounded-lg shadow-lg transition-all duration-200 text-lg ${
              isRecordingGpx 
                ? 'bg-red-600 text-white hover:bg-red-700' 
                : 'bg-white/90 text-gray-700 hover:bg-white'
            }`}
            title={isRecordingGpx ? 'Arrêter l\'enregistrement GPX' : 'Démarrer l\'enregistrement GPX'}
          >
            {isRecordingGpx ? '⏹️' : '🔴'}
          </button>
        )}

        {/* Statistiques GPX */}
        {isRecordingGpx && (
          <div className="bg-black/80 text-white text-xs p-2 rounded-lg">
            <div>📍 {gpxStats.pointCount} pts</div>
            <div>⏱️ {gpxStats.duration}</div>
          </div>
        )}

        {/* Bouton télécharger GPX */}
        {!isRecordingGpx && gpxStats.pointCount > 0 && (
          <button
            onClick={() => {
              try {
                gpxRecorder.downloadGPX();
                // Optionnel : vider après téléchargement
                // gpxRecorder.clear();
                // setGpxStats({ pointCount: 0, duration: '0:00' });
              } catch (error) {
                alert('Erreur lors du téléchargement GPX');
              }
            }}
            className="p-3 bg-green-600 text-white hover:bg-green-700 rounded-lg shadow-lg transition-all duration-200 text-lg"
            title="Télécharger le fichier GPX"
          >
            💾
          </button>
        )}
      </div>
      
      {gpsError && (
        <div className="absolute top-32 left-4 right-4 p-3 bg-red-500/90 text-white rounded-lg text-sm">
          GPS Error: {gpsError}
        </div>
      )}
    </div>
  );
}